import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { BsChevronLeft, BsX } from 'react-icons/bs';
import classNames from 'classnames';
import { useTour } from '@/contexts/Tour.context';
import {
  FIND_TARGET_DELAY,
  FIND_TARGET_RETRIES,
  computeTooltipPosition,
  highlightTourTarget,
} from './Tour.helpers';
import styles from './TourTooltip.module.scss';

const ARROW_CLASS_BY_PLACEMENT = {
  top: 'arrow_bottom',
  bottom: 'arrow_top',
  left: 'arrow_right',
  right: 'arrow_left',
};

function arrowClassFor(placement) {
  return styles[ARROW_CLASS_BY_PLACEMENT[placement] ?? 'arrow_top'];
}

export const TourTooltip = () => {
  const { isActive, steps, currentIndex, next, prev, endTour, callbackUrl } =
    useTour();
  const location = useLocation();
  const tooltipRef = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, visibility: 'hidden' });
  const retryRef = useRef(null);

  const step = isActive && steps.length ? steps[currentIndex] : null;

  const position = useCallback(() => {
    if (!step || !tooltipRef.current) return;

    const tryFind = (attemptsLeft) => {
      const target = document.querySelector(step.target);

      if (!target) {
        if (attemptsLeft > 0) {
          retryRef.current = setTimeout(
            () => tryFind(attemptsLeft - 1),
            FIND_TARGET_DELAY
          );
        }
        return;
      }

      highlightTourTarget(step.target, step.highlight);
      target.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'nearest',
      });

      // Give the scroll/highlight a moment to settle before measuring —
      // scrollIntoView is not synchronous.
      retryRef.current = setTimeout(() => {
        const rect = target.getBoundingClientRect();
        const tooltipHeight = tooltipRef.current?.offsetHeight || 160;
        const pos = computeTooltipPosition(rect, step.placement, tooltipHeight);
        setStyle({ top: pos.top, left: pos.left, visibility: 'visible' });
      }, 350);
    };

    setStyle((s) => ({ ...s, visibility: 'hidden' }));
    clearTimeout(retryRef.current);

    // Some steps need a prior click (e.g. switching an in-page tab) before
    // their target exists — best-effort, no retry if the click target isn't
    // there yet, since the following tryFind loop already tolerates a
    // not-yet-mounted target.
    if (step.clickBefore) {
      document.querySelector(step.clickBefore)?.click();
    }

    retryRef.current = setTimeout(
      () => tryFind(FIND_TARGET_RETRIES),
      FIND_TARGET_DELAY
    );
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-position whenever step changes or tooltip re-renders
  useLayoutEffect(() => {
    position();
    return () => clearTimeout(retryRef.current);
  }, [position]);

  // Re-position after route changes — target only exists on the new page
  useEffect(() => {
    if (!isActive) return;
    const t = setTimeout(position, 100);
    return () => clearTimeout(t);
  }, [location.pathname, isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-position on resize / scroll
  useEffect(() => {
    if (!isActive) return;
    window.addEventListener('resize', position, { passive: true });
    window.addEventListener('scroll', position, {
      passive: true,
      capture: true,
    });
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, { capture: true });
    };
  }, [isActive, position]);

  // Clear the highlight once the tour ends
  useEffect(() => {
    if (!isActive) highlightTourTarget(null);
  }, [isActive]);

  if (!step) return null;

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === steps.length - 1;

  const handleNext = () => {
    if (isLast) {
      endTour();
      if (callbackUrl) window.location.href = callbackUrl;
    } else {
      next(steps.length);
    }
  };

  const tooltip = (
    <div
      ref={tooltipRef}
      className={styles.tooltip}
      style={{ top: style.top, left: style.left, visibility: style.visibility }}
      role="dialog"
      aria-modal="false"
      aria-label={`Tour step ${currentIndex + 1} of ${steps.length}`}
    >
      <div
        className={classNames(styles.arrow, arrowClassFor(step.placement))}
        aria-hidden="true"
      />

      <button
        className={styles.closeBtn}
        onClick={endTour}
        aria-label="Close tour"
      >
        <BsX size={16} />
      </button>

      <p className={styles.content}>{step.content}</p>

      <div className={styles.footer}>
        <button
          className={styles.prevBtn}
          onClick={prev}
          disabled={isFirst}
          aria-label="Previous step"
        >
          <BsChevronLeft size={14} />
        </button>

        <span className={styles.stepCounter}>
          {currentIndex + 1} / {steps.length}
        </span>

        <button className={styles.nextBtn} onClick={handleNext}>
          {isLast ? 'Done' : 'Next'}
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(tooltip, document.body);
};
